import { array, number, object, string } from 'yup';

import type { AddContributorsValidateResponse, Project, ProjectLanguage } from '@app/api/team';
import isEmail from '@app/utils/string/isEmail';

import type {
    InviteNewUserProps,
    InviteNewUserSubmitValues,
    LanguageChangeHandlerPayload,
    LanguageChangeHandlerResults,
    StringSelectOption,
} from './types';
import { AddContributorsModalPage, ConfirmationType, LanguagesFields, Role } from './types';

export const getModalTitle = (modalType: ConfirmationType | AddContributorsModalPage, extraSeats?: number) => {
    switch (modalType) {
        case ConfirmationType.overLimit:
            return 'You may be over your seat limit';

        case ConfirmationType.overLimitNoTrial:
            return 'Your team no longer has any available seats';

        case ConfirmationType.chargeImmediately:
            return 'User you are adding will be assigned to a paid seat';

        case ConfirmationType.chargeImmediatelyNoSeats:
            return extraSeats && extraSeats > 1
                ? `We will add ${extraSeats} paid seats to your subscription`
                : `We will add a paid seat to your subscription`;

        case ConfirmationType.upgradeToEnterprise:
            return 'Upgrade to Enterprise plan to add more users';

        case 'project':
            return 'Add people or groups';

        case 'team':
            return 'Add team users';

        default:
            return 'Add team users';
    }
};

export const getAddButtonLabel = (page: AddContributorsModalPage, contributorsCount: number) => {
    switch (page) {
        case AddContributorsModalPage.team:
            if (contributorsCount > 0) {
                return `Add ${contributorsCount} ${page} user${contributorsCount > 1 ? 's' : ''}`;
            }

            return `Add ${page} user`;

        case AddContributorsModalPage.project:
            if (contributorsCount > 0) {
                return `Add ${contributorsCount} ${page} contributor${contributorsCount > 1 ? 's' : ''}`;
            }

            return `Add ${page} contributor`;

        default:
            return 'Add user';
    }
};

export const getConfirmationType = (
    validation: AddContributorsValidateResponse,
    isProviderAlpha: boolean,
    seatsLimit: number,
    isExtraSeatsAllowed: boolean,
): ConfirmationType | null => {
    const {
        chargeImmediatelyMembers,
        newGracePeriodMembers,
        newInvitedMembers,
        teamSeatsTaken,
        currentInvitedMembersCount,
        currentGracePeriodMembersCount,
        maxSeatCountBeforeUpgrade,
    } = validation;

    const potentialSeatsTaken =
        teamSeatsTaken +
        currentInvitedMembersCount +
        currentGracePeriodMembersCount +
        chargeImmediatelyMembers.length +
        newGracePeriodMembers.length +
        newInvitedMembers.length;

    const isExceedingCap = !isProviderAlpha && potentialSeatsTaken > maxSeatCountBeforeUpgrade;
    const isExceedingLimit = !isProviderAlpha && potentialSeatsTaken > seatsLimit;

    const hasChargeImmediatelyMember = chargeImmediatelyMembers.length > 0;
    const hasGracePeriodMember = newGracePeriodMembers.length > 0;
    const hasInvitedMember = newInvitedMembers.length > 0;

    if (isExceedingCap && isExtraSeatsAllowed) {
        return ConfirmationType.upgradeToEnterprise;
    }

    if (isExceedingLimit && isExtraSeatsAllowed) {
        if (hasGracePeriodMember || hasInvitedMember) {
            return hasChargeImmediatelyMember ? ConfirmationType.overLimitNoTrial : ConfirmationType.overLimit;
        }
        const noSeatsLeft = chargeImmediatelyMembers.length + teamSeatsTaken > seatsLimit;

        return noSeatsLeft ? ConfirmationType.chargeImmediatelyNoSeats : ConfirmationType.chargeImmediately;
    }

    return null;
};

export const extractTeamMembersEmails = (teamMembers: { email: string; text: string }[]) =>
    teamMembers.map((member) => ({
        value: member.email,
        label: member.text,
    }));

const NumberOptionSchema = object().shape({
    label: string(),
    value: number(),
});

const StringOptionSchema = object().shape({
    label: string(),
    value: string(),
});

const languagesSchema = (fields: string[], hasProject?: boolean) =>
    object().shape({
        referenceLanguages: array()
            .of(NumberOptionSchema)
            .nullable()
            .when(fields, {
                is: hasProject
                    ? (role: StringSelectOption, project: StringSelectOption) =>
                          role.value === Role.admin || project === null
                    : (role: StringSelectOption) => role.value === Role.admin,
                then: (schema) => schema.nullable(),
                otherwise: (schema) =>
                    schema
                        .min(1, 'Viewable language is required for Translators')
                        .required('Viewable language is required for Translators'),
            }),
        contributableLanguages: array()
            .of(NumberOptionSchema)
            .nullable()
            .when(fields, {
                is: hasProject
                    ? (role: StringSelectOption, project: StringSelectOption) =>
                          role.value === Role.admin || project === null
                    : (role: StringSelectOption) => role.value === Role.admin,
                then: (schema) => schema.nullable(),
            }),
    });

export const AddExistingUsersFormValidationSchema = object()
    .shape({
        teamMembers: array().of(StringOptionSchema).min(1, 'At least one team member should be selected'),
        role: StringOptionSchema.required('Role is required'),
    })
    .concat(languagesSchema(['role']));

export const InviteNewUserFormSchema = object()
    .shape({
        contributors: array()
            .of(
                string().test(
                    'Email validation',
                    'One or more of the e-mail addresses you provided is not valid.',
                    (value) => isEmail(value || ''),
                ),
            )
            .min(1, 'At least one e-mail address is required'),
        project: StringOptionSchema.nullable().required('Project is required'),
        role: StringOptionSchema.required('Role is required'),
    })
    .concat(languagesSchema(['role', 'project'], true));

export const getInitialLanguagesOptions = (projects: Project[] | Project | undefined) => {
    if (projects === undefined) {
        return [];
    }

    if (!Array.isArray(projects)) {
        return projects.languages.map(({ id, name }) => ({ value: id, label: name }));
    }

    return projects.length === 1 ? projects[0].languages.map(({ id, name }) => ({ value: id, label: name })) : [];
};

export const getInviteNewUserFormLanguageOptions = (projectLanguages: ProjectLanguage[]) =>
    projectLanguages.map(({ id, name }) => ({
        value: id,
        label: name,
    }));

export const getInviteNewUserFormInitialValue = (props: InviteNewUserProps): InviteNewUserSubmitValues => {
    const initialValues = {
        contributors: [],
        project: null,
        role: {
            label: 'Translator',
            value: Role.translator,
        },
        isReviewer: false,
        referenceLanguages: null,
        contributableLanguages: null,
    };
    if (props.page === AddContributorsModalPage.team) {
        const { projects } = props;
        const project = projects?.length === 1 ? { label: projects[0].name, value: projects[0].id } : null;

        return {
            ...initialValues,
            project,
        };
    }

    const { activeProject } = props;

    return {
        ...initialValues,
        project: { label: activeProject.name, value: activeProject.id },
    };
};

export const handleFormLanguageChanges = ({
    field,
    languages,
    values,
}: LanguageChangeHandlerPayload): LanguageChangeHandlerResults => {
    const newContributorLanguage =
        values.contributableLanguages?.filter((language) => !languages.find((item) => item.value === language.value)) ??
        null;
    const newReferenceLanguages =
        values.referenceLanguages?.filter((language) => !languages.find((item) => item.value === language.value)) ??
        null;

    return {
        [LanguagesFields.referenceLanguages]:
            field === LanguagesFields.referenceLanguages ? [...languages] : newReferenceLanguages,
        [LanguagesFields.contributableLanguages]:
            field === LanguagesFields.contributableLanguages ? [...languages] : newContributorLanguage,
    };
};
